import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Currency Context
 * Provides currency management across the app
 * 
 * Primary currency: USD
 * Supported: USD, AED, EUR
 */

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

const CURRENCIES = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD'); // Primary currency
  const [rates, setRates] = useState({ USD: 1.0, AED: 3.6725, EUR: 0.92 });
  const [loading, setLoading] = useState(false);

  // Load rates from API
  useEffect(() => {
    loadRates();
  }, []);

  // Load saved currency preference
  useEffect(() => {
    const saved = localStorage.getItem('preferred_currency');
    if (saved && CURRENCIES[saved]) {
      setCurrency(saved);
    }
  }, []);

  const loadRates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/currency/rates`);
      if (response.data.rates) {
        setRates(response.data.rates);
      }
    } catch (error) {
      console.error('Failed to load currency rates:', error);
    }
  };

  const changeCurrency = (newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('preferred_currency', newCurrency);
    }
  };

  // Format amount in current currency
  const format = (amount, currencyCode = null) => {
    const curr = currencyCode || currency;
    const currInfo = CURRENCIES[curr];
    
    if (!currInfo) return `$${amount.toFixed(2)}`;
    
    return `${currInfo.symbol}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Convert amount from USD to current currency
  const convertFromUSD = (amountUSD) => {
    const rate = rates[currency] || 1;
    return amountUSD * rate;
  };

  // Convert amount to USD from current currency
  const convertToUSD = (amount) => {
    const rate = rates[currency] || 1;
    return amount / rate;
  };

  // Convert between any two currencies
  const convert = async (amount, from, to) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/currency/convert`, {
        amount,
        from_currency: from,
        to_currency: to
      });
      return response.data.converted_amount;
    } catch (error) {
      console.error('Currency conversion error:', error);
      return amount;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currency,
    currencies: CURRENCIES,
    rates,
    loading,
    changeCurrency,
    format,
    convertFromUSD,
    convertToUSD,
    convert,
    symbol: CURRENCIES[currency]?.symbol || '$',
    name: CURRENCIES[currency]?.name || 'US Dollar'
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
